import React, { ComponentType } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, Calendar, MapPin, Star, ShoppingBag, TrendingUp, Clock, Award, MessageSquare, Heart, QrCode, ShoppingCart, Bike, CalendarClock } from 'lucide-react';
import { Customer, CustomerTag, getCustomerMetrics, tagMeta, useCRM, methodLabels } from './CRMContext';
import { Branch } from './BranchesContext';
import { Toggle } from './Toggle';
import { TagPill } from './CRMPage';
const allTags: CustomerTag[] = ['vip', 'regular', 'new', 'dormant', 'birthday-month'];
const methodIcons: Record<string, ComponentType<{
  size?: number;
  className?: string;
}>> = {
  dineIn: QrCode,
  takeout: ShoppingCart,
  delivery: Bike,
  orderAhead: CalendarClock
};
const methodColors: Record<string, string> = {
  dineIn: 'bg-purple-100 text-purple-700',
  takeout: 'bg-orange-100 text-orange-700',
  delivery: 'bg-blue-100 text-blue-700',
  orderAhead: 'bg-emerald-100 text-emerald-700'
};
export function CustomerProfilePanel({
  customer,
  onClose,
  branches
}: {
  customer: Customer | null;
  onClose: () => void;
  branches: Branch[];
}) {
  const {
    updateCustomer,
    toggleTag
  } = useCRM();
  return <AnimatePresence data-id="element-1726">
      {customer && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/30 z-40" data-id="element-1727" />
          {/* Drawer */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col" data-id="element-1728">
            <ProfileContent customer={customer} onClose={onClose} branches={branches} updateCustomer={updateCustomer} toggleTag={toggleTag} data-id="element-1729" />
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function ProfileContent({
  customer,
  onClose,
  branches,
  updateCustomer,
  toggleTag
}: {
  customer: Customer;
  onClose: () => void;
  branches: Branch[];
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  toggleTag: (id: string, tag: CustomerTag) => void;
}) {
  const m = getCustomerMetrics(customer);
  const initials = (customer.firstName[0] || '') + (customer.lastName[0] || '');
  const age = customer.birthDate ? Math.floor((Date.now() - new Date(customer.birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25)) : null;
  const tenureDays = Math.floor((Date.now() - new Date(customer.joined).getTime()) / (1000 * 60 * 60 * 24));
  const preferredBranch = branches.find(b => b.id === customer.preferredBranchId);
  const sortedOrders = [...customer.orders].sort((a, b) => b.date.localeCompare(a.date));
  return <>
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3 flex-shrink-0" data-id="element-1730">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-200 to-orange-400 text-white flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-sm" data-id="element-1731">
          {initials.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0" data-id="element-1732">
          <h2 className="text-lg font-bold text-gray-900 truncate" data-id="element-1733">
            {customer.firstName} {customer.lastName}
          </h2>
          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-1" data-id="element-1734">
            {customer.sex !== 'unspecified' && <span className="uppercase font-semibold" data-id="element-1735">
                {customer.sex === 'male' ? 'Male' : 'Female'}
              </span>}
            {age !== null && <>
                <span className="text-gray-300" data-id="element-1736">•</span>
                <span data-id="element-1737">{age} years old</span>
              </>}
            <span className="text-gray-300" data-id="element-1738">•</span>
            <span data-id="element-1739">
              Member for {tenureDays} day{tenureDays === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-2 flex-wrap" data-id="element-1740">
            {customer.tags.map(t => <TagPill key={t} tag={t} data-id="element-1741" />)}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0" aria-label="Close" data-id="element-1742">
          <X size={18} data-id="element-1743" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-id="element-1744">
        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2" data-id="element-1745">
          <MiniStat icon={<ShoppingBag size={13} data-id="element-1747" />} label="Total orders" value={m.totalOrders.toString()} data-id="element-1746" />
          <MiniStat icon={<TrendingUp size={13} data-id="element-1749" />} label="Lifetime spend" value={`ETB ${Math.round(m.lifetimeSpend).toLocaleString()}`} data-id="element-1748" />
          <MiniStat icon={<Award size={13} data-id="element-1751" />} label="Avg order" value={`ETB ${Math.round(m.avgOrderValue).toLocaleString()}`} data-id="element-1750" />
          <MiniStat icon={<Clock size={13} data-id="element-1753" />} label="Last order" value={m.daysSinceLastOrder !== null ? m.daysSinceLastOrder === 0 ? 'Today' : `${m.daysSinceLastOrder}d ago` : 'Never'} highlight={m.daysSinceLastOrder !== null && m.daysSinceLastOrder > 60 ? 'warning' : undefined} data-id="element-1752" />
        </div>

        {/* Rating */}
        {m.rating !== null && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-2" data-id="element-1754">
            <Star size={14} className="text-amber-500 fill-amber-400" data-id="element-1755" />
            <span className="text-sm font-bold text-amber-900 tabular-nums" data-id="element-1756">
              {m.rating.toFixed(1)}
            </span>
            <span className="text-xs text-amber-700" data-id="element-1757">avg rating given</span>
          </div>}

        {/* Contact info */}
        <Section title="Contact" data-id="element-1758">
          <InfoRow icon={<Phone size={13} data-id="element-1760" />} label="Phone" data-id="element-1759">
            <span className="tabular-nums" data-id="element-1761">{customer.phone}</span>
          </InfoRow>
          {customer.email && <InfoRow icon={<Mail size={13} data-id="element-1763" />} label="Email" data-id="element-1762">
              {customer.email}
            </InfoRow>}
          {customer.birthDate && <InfoRow icon={<Calendar size={13} data-id="element-1765" />} label="Birthday" data-id="element-1764">
              {new Date(customer.birthDate).toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
            </InfoRow>}
          <InfoRow icon={<Calendar size={13} data-id="element-1767" />} label="Joined" data-id="element-1766">
            {new Date(customer.joined).toLocaleString()}
          </InfoRow>
          {preferredBranch && <InfoRow icon={<MapPin size={13} data-id="element-1769" />} label="Preferred branch" data-id="element-1768">
              {preferredBranch.name}
            </InfoRow>}
        </Section>

        {/* Behavior */}
        <Section title="Behavior" data-id="element-1770">
          {m.favoriteMethod && <InfoRow icon={<Heart size={13} data-id="element-1772" />} label="Favorite channel" data-id="element-1771">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${methodColors[m.favoriteMethod]}`} data-id="element-1773">
                {methodLabels[m.favoriteMethod]}
              </span>
            </InfoRow>}
          {m.topItems.length > 0 && <div className="pt-2" data-id="element-1774">
              <div className="text-[10px] uppercase tracking-wide font-bold text-gray-500 mb-1.5" data-id="element-1775">
                Most ordered
              </div>
              <div className="space-y-1" data-id="element-1776">
                {m.topItems.map((it, i) => <div key={it.name} className="flex items-center justify-between text-sm" data-id="element-1777">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-1778">
                      <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-1779">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 truncate" data-id="element-1780">{it.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 tabular-nums flex-shrink-0" data-id="element-1781">
                      ×{it.count}
                    </span>
                  </div>)}
              </div>
            </div>}
        </Section>

        {/* Tags */}
        <Section title="Tags" data-id="element-1782">
          <div className="flex items-center gap-1.5 flex-wrap" data-id="element-1783">
            {allTags.map(tag => {
            const isActive = customer.tags.includes(tag);
            const meta = tagMeta[tag];
            return <button key={tag} onClick={() => toggleTag(customer.id, tag)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border transition-colors ${isActive ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-1784">
                  {meta.label}
                </button>;
          })}
          </div>
        </Section>

        {/* Marketing */}
        <Section title="Marketing" data-id="element-1785">
          <div className="flex items-center justify-between gap-3" data-id="element-1786">
            <div data-id="element-1787">
              <div className="text-sm font-semibold text-gray-900" data-id="element-1788">
                Promotional messages
              </div>
              <div className="text-[11px] text-gray-500" data-id="element-1789">
                Receive offers, birthday rewards, and updates
              </div>
            </div>
            <Toggle checked={customer.marketingOptIn} onChange={v => updateCustomer(customer.id, {
            marketingOptIn: v
          })} data-id="element-1790" />
          </div>
        </Section>

        {/* Notes */}
        <Section title="Internal notes" data-id="element-1791">
          <div className="relative" data-id="element-1792">
            <MessageSquare size={13} className="absolute left-2.5 top-2.5 text-gray-400" data-id="element-1793" />
            <textarea value={customer.notes} onChange={e => updateCustomer(customer.id, {
            notes: e.target.value
          })} placeholder="Allergies, preferences, special requests..." rows={3} className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition resize-none" data-id="element-1794" />
          </div>
          <p className="text-[10px] text-gray-400 mt-1" data-id="element-1795">
            Visible to staff only — never shown to the customer.
          </p>
        </Section>

        {/* Order history */}
        <Section title="Order history" right={<span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-1797">
              {sortedOrders.length} total
            </span>} data-id="element-1796">
          {sortedOrders.length === 0 ? <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-xs text-gray-400" data-id="element-1798">
              No orders yet.
            </div> : <div className="space-y-2" data-id="element-1799">
              {sortedOrders.map(o => <OrderHistoryRow key={o.id} order={o} branches={branches} data-id="element-1800" />)}
            </div>}
        </Section>
      </div>
    </>;
}
function MiniStat({
  icon,
  label,
  value,
  highlight
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: 'warning';
}) {
  return <div className={`rounded-lg border p-2.5 ${highlight === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`} data-id="element-1801">
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-0.5" data-id="element-1802">
        <span className={`${highlight === 'warning' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-1803">
          {icon}
        </span>
        {label}
      </div>
      <div className={`text-base font-bold tabular-nums ${highlight === 'warning' ? 'text-amber-900' : 'text-gray-900'}`} data-id="element-1804">
        {value}
      </div>
    </div>;
}
function Section({
  title,
  children,
  right
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return <section data-id="element-1805">
      <div className="flex items-center justify-between mb-2" data-id="element-1806">
        <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1807">
          {title}
        </h3>
        {right}
      </div>
      <div className="space-y-1.5" data-id="element-1808">{children}</div>
    </section>;
}
function InfoRow({
  icon,
  label,
  children
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="grid grid-cols-[20px_90px_1fr] items-center gap-2 text-sm py-1" data-id="element-1809">
      <span className="text-gray-400" data-id="element-1810">{icon}</span>
      <span className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold" data-id="element-1811">
        {label}
      </span>
      <span className="text-gray-900 truncate" data-id="element-1812">{children}</span>
    </div>;
}
function OrderHistoryRow({
  order,
  branches
}: {
  order: ReturnType<typeof getCustomerMetrics> extends infer T ? any : never; // sidestep import cycle; use Customer['orders'][number]
  branches: Branch[];
}) {
  const Icon = methodIcons[order.method];
  const branch = branches.find(b => b.id === order.branchId);
  const d = new Date(order.date);
  const daysAgo = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  const dateLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`;
  const itemSummary = order.items.slice(0, 2).map((i: any) => `${i.qty}× ${i.name}`).join(', ');
  const extraItems = order.items.length - 2;
  return <div className="rounded-lg border border-gray-200 bg-white p-2.5" data-id="element-1813">
      <div className="flex items-start gap-2.5" data-id="element-1814">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${methodColors[order.method]}`} data-id="element-1815">
          <Icon size={13} data-id="element-1816" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1817">
          <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-1818">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide" data-id="element-1819">
              <span className="text-gray-700" data-id="element-1820">
                {methodLabels[order.method]}
              </span>
              {order.status === 'cancelled' && <span className="bg-red-100 text-red-700 px-1 rounded" data-id="element-1821">
                  Cancelled
                </span>}
              {order.status === 'refunded' && <span className="bg-gray-100 text-gray-700 px-1 rounded" data-id="element-1822">
                  Refunded
                </span>}
            </div>
            <div className="font-bold text-gray-900 text-sm tabular-nums flex-shrink-0" data-id="element-1823">
              ETB {Math.round(order.total).toLocaleString()}
            </div>
          </div>
          <div className="text-xs text-gray-700 truncate" data-id="element-1824">
            {itemSummary}
            {extraItems > 0 && <span className="text-gray-400" data-id="element-1825"> +{extraItems} more</span>}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500" data-id="element-1826">
            <span data-id="element-1827">{dateLabel}</span>
            {branch && <>
                <span className="text-gray-300" data-id="element-1828">•</span>
                <span className="truncate" data-id="element-1829">{branch.name}</span>
              </>}
            {order.rating && <>
                <span className="text-gray-300" data-id="element-1830">•</span>
                <span className="inline-flex items-center gap-0.5 text-amber-600 font-bold" data-id="element-1831">
                  <Star size={9} className="fill-amber-400" data-id="element-1832" />
                  {order.rating}
                </span>
              </>}
          </div>
        </div>
      </div>
    </div>;
}